import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Plus, Pencil, Trash2, X } from 'lucide-react'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { getProducts, getCategories, createProduct, updateProduct, deactivateProduct, uploadProductImage } from '../../api/products'
import { formatMoney } from '../../utils/format'
import { useAuth } from '../../context/AuthContext'
import { useT } from '../../i18n'
import { Product } from '../../types'

const API = import.meta.env.VITE_API_URL ?? ''

interface Form {
  name_ru: string; name_uz: string; price: string
  category_id: string; description_ru: string; description_uz: string; image_path: string
  extra_images: string[]; notify_clients: boolean
}

const EMPTY: Form = { name_ru: '', name_uz: '', price: '', category_id: '', description_ru: '', description_uz: '', image_path: '', extra_images: [], notify_clients: false }

export default function ProductsPage() {
  const { user } = useAuth()
  const lang = user?.language ?? 'ru'
  const tr = useT(lang)
  const qc = useQueryClient()

  const [editing, setEditing] = useState<Product | null | 'new'>(null)
  const [form, setForm] = useState<Form>(EMPTY)
  const [uploading, setUploading] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['products-admin'],
    queryFn: () => getProducts({ is_active: undefined as any, limit: 200 }),
  })

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['products-admin'] })
    qc.invalidateQueries({ queryKey: ['products'] })
  }

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        name_ru: form.name_ru, name_uz: form.name_uz,
        price: Number(form.price),
        category_id: form.category_id ? Number(form.category_id) : undefined,
        description_ru: form.description_ru || undefined,
        description_uz: form.description_uz || undefined,
        image_path: form.image_path || undefined,
        extra_images: form.extra_images,
        notify_clients: editing === 'new' ? form.notify_clients : undefined,
      }
      if (editing && editing !== 'new') return updateProduct(editing.id, payload)
      return createProduct(payload)
    },
    onSuccess: () => { toast.success(editing === 'new' ? tr('productAdded') : tr('productSaved')); setEditing(null); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deactivateProduct(id),
    onSuccess: () => { toast.success(tr('productDeactivated')); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  function openNew() {
    setForm(EMPTY)
    setEditing('new')
  }

  function openEdit(p: Product) {
    setForm({
      name_ru: p.name_ru, name_uz: p.name_uz, price: String(p.price),
      category_id: p.category_id ? String(p.category_id) : '',
      description_ru: p.description_ru ?? '', description_uz: p.description_uz ?? '',
      image_path: p.image_path ?? '',
      extra_images: p.extra_images ?? [],
      notify_clients: false,
    })
    setEditing(p)
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const res = await uploadProductImage(file)
      setForm((f) => ({ ...f, image_path: res.image_path }))
      toast.success(tr('photoUploaded'))
    } catch {
      toast.error(tr('uploadError'))
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  async function handleExtraImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const res = await uploadProductImage(file)
      setForm((f) => ({ ...f, extra_images: [...f.extra_images, res.image_path] }))
      toast.success(tr('photoUploaded'))
    } catch {
      toast.error(tr('uploadError'))
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  function removeExtraImage(idx: number) {
    setForm((f) => ({ ...f, extra_images: f.extra_images.filter((_, i) => i !== idx) }))
  }

  if (editing) {
    return (
      <Layout title={editing === 'new' ? tr('add') : tr('edit')} hideNav>
        <button onClick={() => setEditing(null)} className="flex items-center gap-1 text-tg-link mb-4">
          <X size={16} /> {tr('cancel')}
        </button>
        <div className="flex flex-col gap-3">
          <Field label={tr('nameRu')} value={form.name_ru} onChange={(v) => setForm(f => ({ ...f, name_ru: v }))} />
          <Field label={tr('nameUz')} value={form.name_uz} onChange={(v) => setForm(f => ({ ...f, name_uz: v }))} />
          <Field label={`${tr('price')} ($)`} type="number" value={form.price} onChange={(v) => setForm(f => ({ ...f, price: v }))} />

          <div>
            <p className="section-title">{tr('category')}</p>
            <select className="input mt-1" value={form.category_id} onChange={(e) => setForm(f => ({ ...f, category_id: e.target.value }))}>
              <option value="">{tr('noCategory')}</option>
              {categories?.map((c) => (
                <option key={c.id} value={c.id}>{c.emoji} {lang === 'uz' ? c.name_uz : c.name_ru}</option>
              ))}
            </select>
          </div>

          <Field label={tr('descriptionRu')} value={form.description_ru} onChange={(v) => setForm(f => ({ ...f, description_ru: v }))} multiline />
          <Field label={tr('descriptionUz')} value={form.description_uz} onChange={(v) => setForm(f => ({ ...f, description_uz: v }))} multiline />

          <div>
            <p className="section-title mb-2">{tr('productPhoto')}</p>
            {/* Main photo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              {form.image_path ? (
                <div style={{ position: 'relative' }}>
                  <img src={`${API}/uploads/${form.image_path}`} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 10 }} />
                  <button
                    onClick={() => setForm(f => ({ ...f, image_path: '' }))}
                    style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#D97757', border: 'none', color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >×</button>
                </div>
              ) : (
                <label style={{ width: 72, height: 72, borderRadius: 10, border: '1.5px dashed rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                  <span style={{ fontSize: 22 }}>📷</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploading} />
                </label>
              )}
              <div>
                <p style={{ fontSize: 11, color: '#A8A8AE' }}>Главное фото</p>
                {!form.image_path && <p style={{ fontSize: 10, color: '#8A8A96' }}>Нажмите для загрузки</p>}
                {form.image_path && (
                  <label style={{ fontSize: 11, color: '#F97316', cursor: 'pointer' }}>
                    Заменить
                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploading} />
                  </label>
                )}
              </div>
            </div>

            {/* Extra photos */}
            <p style={{ fontSize: 10, color: '#8A8A96', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Дополнительные фото</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {form.extra_images.map((img, idx) => (
                <div key={idx} style={{ position: 'relative' }}>
                  <img src={`${API}/uploads/${img}`} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />
                  <button
                    onClick={() => removeExtraImage(idx)}
                    style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#D97757', border: 'none', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >×</button>
                </div>
              ))}
              <label style={{ width: 56, height: 56, borderRadius: 8, border: '1.5px dashed rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <span style={{ fontSize: 18, color: '#8A8A96' }}>+</span>
                <input type="file" accept="image/*" onChange={handleExtraImageUpload} style={{ display: 'none' }} disabled={uploading} />
              </label>
            </div>
            {uploading && <p className="text-xs text-tg-hint mt-1">{tr('loading')}</p>}
          </div>

          {editing === 'new' && (
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, notify_clients: !f.notify_clients }))}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', borderRadius: 12, width: '100%',
                background: form.notify_clients ? 'rgba(249,115,22,0.08)' : '#131316',
                border: form.notify_clients ? '1px solid rgba(249,115,22,0.30)' : '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer',
              }}
            >
              <div style={{
                width: 38, height: 22, borderRadius: 99, flexShrink: 0,
                background: form.notify_clients ? '#F97316' : 'rgba(255,255,255,0.10)',
                position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  position: 'absolute', top: 3, left: form.notify_clients ? 19 : 3,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s',
                }} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <p style={{ fontSize: 13, color: form.notify_clients ? '#F97316' : '#F5F4F0', fontWeight: 500 }}>
                  Уведомить всех клиентов
                </p>
                <p style={{ fontSize: 11, color: '#8A8A96', marginTop: 2 }}>
                  Отправить сообщение о новом товаре в Telegram
                </p>
              </div>
            </button>
          )}

          <button
            onClick={() => saveMut.mutate()}
            disabled={!form.name_ru || !form.price || saveMut.isPending}
            className="btn-primary mt-2 disabled:opacity-40"
          >
            {tr('save')}
          </button>
        </div>
      </Layout>
    )
  }

  return (
    <Layout title={tr('products')}>
      <button onClick={openNew} className="btn-primary mb-4 flex items-center justify-center gap-2">
        <Plus size={18} /> {tr('add')}
      </button>

      {isLoading ? (
        <Spinner className="mt-10" />
      ) : !data?.items.length ? (
        <p className="text-center text-tg-hint mt-10">{tr('noProducts')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {data.items.map((p) => (
            <div key={p.id} className="card flex gap-3 items-center">
              {p.image_path ? (
                <img src={`${API}/uploads/${p.image_path}`} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-tg-hint/10 flex items-center justify-center text-xl flex-shrink-0">📦</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-tg-text text-sm line-clamp-1">{lang === 'uz' ? p.name_uz : p.name_ru}</p>
                <p className="text-xs text-tg-btn">{formatMoney(p.price)}</p>
                {!p.is_active && <p className="text-xs text-tg-danger">{tr('inactive')}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(p)} className="p-2 rounded-lg bg-tg-secondary text-tg-hint">
                  <Pencil size={15} />
                </button>
                <button
                  onClick={() => { if (confirm(tr('deactivateConfirm'))) deleteMut.mutate(p.id) }}
                  className="p-2 rounded-lg"
                  style={{ background: 'rgba(217,119,87,0.10)', color: '#D97757' }}
                  title={tr('deactivate')}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  )
}

function Field({ label, value, onChange, type = 'text', multiline }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; multiline?: boolean
}) {
  return (
    <div>
      <p className="section-title">{label}</p>
      {multiline ? (
        <textarea className="input mt-1 resize-none" rows={2} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} className="input mt-1" value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  )
}
